import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {type RunSetQuery} from '../../../../util/queryTypes';
import {type BucketedQueryType} from './../queries/queryVars';

/*
This function selects one of six active query types based on runtime conditions:

1. **ID Type** (Id suffix):
   - Standard queries use projectId/entityId from the current workspace/report context
   - "Id" suffix queries use internalProjectId for cross-project references (e.g., in reports with multiple runsets)

2. **Grouping** (Grouped prefix):
   - Standard queries fetch individual runs
   - "Grouped" queries leverage backend aggregation when enabled, reducing frontend processing load
   - Grouped queries are only used when: backend grouping is enabled, querying a single runset,
     displaying grouped data, and not querying system metrics

3. **Line Construction** (BackendLines infix):
   - Standard queries use frontend line construction
   - "BackendLines" queries use backend-constructed line data for improved performance
   - Backend line construction requires BOTH usesBackendLineConstruction AND allowsBackendLineConstruction
   - Backend line construction is NOT supported with internal ID queries (no BucketedBackendLinesQueryId)

4. **Shadowing** (Shadowed prefix):
   - "Shadowed" queries run both backend and frontend queries in parallel for monitoring/validation
   - Only available when backend line construction requirements are met (standard ID + feature support)

Active Query Types (6):
- BucketedQuery (baseline)
- BucketedQueryId (baseline + internal ID)
- GroupedBucketedQuery (baseline + backend grouping)
- GroupedBucketedQueryId (baseline + backend grouping + internal ID)
- BucketedBackendLinesQuery (baseline + backend line construction, standard ID only)
- ShadowedBackendLinesQuery (backend lines + monitoring, standard ID only)

Unsupported:
- BucketedBackendLinesQueryId - Backend line construction with internal ID is NOT supported

See architecture.md for full documentation.
*/
export const getQueryType = ({
  allowsBackendLineConstruction,
  enableBackendGrouping,
  historyKeys,
  isGrouped,
  runSets,
  usesBackendLineConstruction,
  usesShadowedBackendLinesQuery,
  usesInternalId,
}: {
  allowsBackendLineConstruction: boolean;
  enableBackendGrouping: boolean;
  historyKeys: string[];
  isGrouped: boolean;
  runSets: RunSetQuery[];
  usesBackendLineConstruction: boolean;
  usesShadowedBackendLinesQuery: boolean;
  usesInternalId: boolean;
}): BucketedQueryType => {
  const useGroupedQuery =
    enableBackendGrouping &&
    runSets.length < 2 &&
    isGrouped &&
    !historyKeys.some(key => isSystemMetric(key));

  if (useGroupedQuery) {
    if (
      usesBackendLineConstruction &&
      allowsBackendLineConstruction &&
      !usesInternalId
    ) {
      return 'GroupedBucketedBackendLinesQuery';
    }
    return usesInternalId ? 'GroupedBucketedQueryId' : 'GroupedBucketedQuery';
  }

  if (
    usesBackendLineConstruction &&
    allowsBackendLineConstruction &&
    !usesInternalId
  ) {
    return usesShadowedBackendLinesQuery
      ? 'ShadowedBackendLinesQuery'
      : 'BucketedBackendLinesQuery';
  }

  return usesInternalId ? 'BucketedQueryId' : 'BucketedQuery';
};
